import { useCallback, useEffect, useSyncExternalStore } from 'react';

/**
 * Tema claro, escuro ou o do sistema.
 *
 * A escolha fica no navegador, por aparelho: quem usa o sistema no celular sob
 * sol e no escritório à noite escolhe em cada um. "Sistema" segue a preferência
 * do aparelho e muda junto com ela, sem recarregar.
 *
 * O CSS só conhece `data-theme="dark"` (ver `theme.css`): é este módulo que
 * resolve "sistema" para um dos dois e escreve o atributo. O mesmo cálculo
 * roda em `index.html`, antes da primeira pintura, para a página não piscar
 * clara antes de escurecer.
 */
export type ThemePreference = 'light' | 'dark' | 'system';

export const THEME_STORAGE_KEY = 'obras:tema';

const PREFERENCES: readonly ThemePreference[] = ['light', 'dark', 'system'];

const listeners = new Set<() => void>();

function readPreference(): ThemePreference {
  try {
    const raw = window.localStorage.getItem(THEME_STORAGE_KEY);
    return PREFERENCES.includes(raw as ThemePreference) ? (raw as ThemePreference) : 'system';
  } catch {
    return 'system';
  }
}

function systemPrefersDark(): boolean {
  return window.matchMedia('(prefers-color-scheme: dark)').matches;
}

export function resolveTheme(preference: ThemePreference): 'light' | 'dark' {
  if (preference === 'system') return systemPrefersDark() ? 'dark' : 'light';
  return preference;
}

/** Escreve o tema resolvido no documento. Idempotente. */
export function applyTheme(preference: ThemePreference): void {
  const resolved = resolveTheme(preference);
  document.documentElement.dataset['theme'] = resolved;

  /*
   * A cor da barra do navegador acompanha o fundo da página: um celular com o
   * sistema em tema escuro e a barra branca em cima parece outro aplicativo.
   */
  const meta = document.querySelector<HTMLMetaElement>('meta[name="theme-color"]');
  if (meta) meta.content = resolved === 'dark' ? '#0f0f0f' : '#f5f5f5';
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);

  const media = window.matchMedia('(prefers-color-scheme: dark)');
  const onSystemChange = () => {
    if (readPreference() === 'system') applyTheme('system');
    listener();
  };
  media.addEventListener('change', onSystemChange);

  return () => {
    listeners.delete(listener);
    media.removeEventListener('change', onSystemChange);
  };
}

/**
 * Só o tema em vigor, para quem precisa escolher um recurso por ele — a marca,
 * que tem uma arte para fundo claro e outra para fundo escuro. Não escreve
 * nada no documento; isso é do `useTheme`, montado uma vez na raiz.
 */
export function useResolvedTheme(): 'light' | 'dark' {
  return useSyncExternalStore(
    subscribe,
    () => resolveTheme(readPreference()),
    () => 'light' as const,
  );
}

export function useTheme(): {
  preference: ThemePreference;
  resolved: 'light' | 'dark';
  setPreference: (preference: ThemePreference) => void;
} {
  const preference = useSyncExternalStore(subscribe, readPreference, () => 'system' as const);

  useEffect(() => {
    applyTheme(preference);
  }, [preference]);

  const setPreference = useCallback((next: ThemePreference) => {
    try {
      window.localStorage.setItem(THEME_STORAGE_KEY, next);
    } catch {
      // Sem armazenamento, a escolha vale enquanto a aba está aberta.
    }
    applyTheme(next);
    for (const listener of listeners) listener();
  }, []);

  return { preference, resolved: resolveTheme(preference), setPreference };
}
